/**
 * API Configuration
 * Environment-specific API endpoints and settings
 * Now using serverless infrastructure with 94% cost reduction
 */

// Serverless API configuration
const serverlessApiConfig = {
  apiEndpoint: 'https://api-prod.telemetry.briefcasebrain.com',
  webEndpoint: 'https://telemetry.briefcasebrain.com'
};

export interface ApiConfig {
  baseURL: string;
  wsURL: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
  enableMocking: boolean;
  enableCaching: boolean;
  enableDeduplication: boolean;
  enableLogging: boolean;
  enableRealTime: boolean;
}

export interface EndpointConfig {
  auth: string;
  overview: string;
  metrics: string;
  experiments: string;
  deployments: string;
  reports: string;
  appsync: string;
  lakefs: string;
}

// Environment detection
const getEnvironment = (): 'development' | 'staging' | 'production' => {
  if (typeof window === 'undefined') return 'development';

  const hostname = window.location.hostname;

  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'development';
  } else if (hostname.includes('staging') || hostname.includes('dev')) {
    return 'staging';
  } else {
    return 'production';
  }
};

// Environment-specific configurations
// NOW USING SERVERLESS BACKEND - 94% cost reduction achieved
const environments = {
  development: {
    baseURL: serverlessApiConfig.apiEndpoint || 'http://localhost:3002',
    wsURL: serverlessApiConfig.apiEndpoint?.replace('https://', 'wss://').replace('http://', 'ws://') + '/ws' || 'ws://localhost:3002/ws',
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
    enableMocking: false,
    enableCaching: false,
    enableDeduplication: true,
    enableLogging: true,
    enableRealTime: true,
  },
  staging: {
    baseURL: 'https://api-staging.telemetry.briefcasebrain.com',
    wsURL: 'wss://api-staging.telemetry.briefcasebrain.com/ws',
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
    enableMocking: false,
    enableCaching: true,
    enableDeduplication: true,
    enableLogging: true,
    enableRealTime: true,
  },
  production: {
    baseURL: serverlessApiConfig.apiEndpoint || 'https://api-prod.telemetry.briefcasebrain.com',
    wsURL: serverlessApiConfig.apiEndpoint?.replace('https://', 'wss://') + '/ws' || 'wss://api-prod.telemetry.briefcasebrain.com/ws',
    timeout: 30000,
    retryAttempts: 3,
    retryDelay: 1000,
    enableMocking: false,
    enableCaching: true,
    enableDeduplication: true,
    enableLogging: false,
    enableRealTime: true,
  },
} as const;

// API endpoints
const endpoints: EndpointConfig = {
  auth: '/api/auth',
  overview: '/api/v1/overview',
  metrics: '/api/v1/metrics',
  experiments: '/api/v1/experiments',
  deployments: '/api/v1/deployments',
  reports: '/api/v1/impact',
  appsync: import.meta.env.VITE_APPSYNC_URL || 'wss://appsync.briefcasebrain.com/graphql',
  lakefs: import.meta.env.VITE_LAKEFS_URL || 'https://lakefs.briefcasebrain.com',
};

// Get current environment configuration
export const getCurrentEnvironment = () => getEnvironment();

export const getApiConfig = (): ApiConfig => {
  const env = getEnvironment();
  const config = environments[env];

  // Override with environment variables if available
  return {
    ...config,
    baseURL: import.meta.env.VITE_API_URL || config.baseURL,
    wsURL: import.meta.env.VITE_WS_URL || config.wsURL,
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || String(config.timeout)),
    retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || String(config.retryAttempts)),
    enableMocking: import.meta.env.VITE_ENABLE_MOCKING === 'true' || config.enableMocking,
    enableCaching: import.meta.env.VITE_ENABLE_CACHING === 'true' || config.enableCaching,
    enableLogging: import.meta.env.VITE_ENABLE_LOGGING === 'true' || config.enableLogging,
    enableRealTime: import.meta.env.VITE_ENABLE_REAL_TIME !== 'false' && config.enableRealTime,
  };
};

export const getEndpoints = (): EndpointConfig => endpoints;

// Feature flags
export const getFeatureFlags = () => ({
  enableRealTimeUpdates: import.meta.env.VITE_ENABLE_REAL_TIME !== 'false',
  enableExperimentalFeatures: import.meta.env.VITE_ENABLE_EXPERIMENTAL === 'true',
  enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS !== 'false',
  enableErrorTracking: import.meta.env.VITE_ENABLE_ERROR_TRACKING !== 'false',
  maxFileUploadSize: parseInt(import.meta.env.VITE_MAX_FILE_UPLOAD_SIZE || '10485760'), // 10MB
  sessionTimeoutMinutes: parseInt(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || '30'),
  warningTimeMinutes: parseInt(import.meta.env.VITE_WARNING_TIME_MINUTES || '5'),
});

// Regional settings
export const getRegionalConfig = () => ({
  currency: import.meta.env.VITE_CURRENCY || 'USD',
  timezone: import.meta.env.VITE_TIMEZONE || Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale: import.meta.env.VITE_LOCALE || 'en-US',
  dateFormat: import.meta.env.VITE_DATE_FORMAT || 'MM/dd/yyyy',
  timeFormat: import.meta.env.VITE_TIME_FORMAT || '12h',
});

// Validation
export const validateApiConfig = (config: ApiConfig): boolean => {
  if (!config.baseURL) {
    console.error('API configuration error: baseURL is required');
    return false;
  }

  try {
    new URL(config.baseURL);
  } catch {
    console.error('API configuration error: invalid baseURL');
    return false;
  }

  if (!config.wsURL) {
    console.error('API configuration error: wsURL is required');
    return false;
  }

  // Validate WebSocket URL format
  if (!config.wsURL.startsWith('ws://') && !config.wsURL.startsWith('wss://')) {
    console.error('API configuration error: wsURL must start with ws:// or wss://');
    return false;
  }

  if (config.timeout <= 0) {
    console.error('API configuration error: timeout must be positive');
    return false;
  }

  if (config.retryAttempts < 0) {
    console.error('API configuration error: retryAttempts must be non-negative');
    return false;
  }

  return true;
};

// Export singleton config objects
export const apiConfig = getApiConfig();
export const apiEndpoints = getEndpoints();
export const featureFlags = getFeatureFlags();
export const regionalConfig = getRegionalConfig();

// Validate configuration on load
if (!validateApiConfig(apiConfig)) {
  throw new Error('Invalid API configuration');
}
